import { z } from 'zod';
import { termDurationSchema } from '../../common';
import {
  dateSchema,
  idSchema,
  nameSchema,
  objectIdSchema,
  percentageSchema,
  pricePositiveSchema,
} from '../../validation';
import {
  financingDeclineReasonSchema,
  financingOfferDecisionSchema,
} from './financing.enums';

/**
 * Concrete offer terms a lender commits to. Present once the
 * lender has submitted terms (`decision === 'offered'`) and
 * stays present through `'accepted'` — those terms are exactly
 * what the carrier locked in. Absent for `'pending'`,
 * `'interested'`, and `'declined'` rows.
 *
 * Server-side computes `isCounterOffer` by comparing these to
 * the application's `requestedAmount` / `requestedTerm`.
 */
export const financingOfferTermsSchema = z.object({
  /** How much the lender will advance the carrier (typically 80–95% of invoice). */
  advancedAmount: pricePositiveSchema.refine(v => v > 0, {
    message: 'advancedAmount must be greater than zero',
  }),
  /** Fee charged on top of the advance. Drives the carrier's net cost. */
  feeAmount: pricePositiveSchema.refine(v => v > 0, {
    message: 'feeAmount must be greater than zero',
  }),
  /**
   * Fee as a fraction (`0.04` = 4%). Quoted against the carrier's
   * **requested amount** (the invoice face value the carrier is
   * factoring), NOT the advance the lender is putting up — this is
   * the invoice-factoring convention and keeps rates comparable
   * across offers regardless of advance level.
   *
   * UI math is `feeAmount = feePercentage × application.requestedAmount`
   * in both the lender-view offer form and the admin Edit Offer
   * dialog. The stored value is base-agnostic; the convention only
   * shows up in how forms derive `feeAmount` and how consumers
   * back-compute the rate from the two stored numbers.
   */
  feePercentage: percentageSchema.refine(v => v > 0, {
    message: 'feePercentage must be greater than zero',
  }),
  /** Repayment term (30/60/90 days, 6 months, 1 year). */
  term: termDurationSchema,
  /**
   * Explicit repayment date the lender expects the carrier to settle
   * by. Required on every offer write (lender partner-view + admin
   * back-fill). Read paths may still fall back to `term` / funding
   * date for legacy rows missing this field.
   */
  repaymentDate: dateSchema,
  /**
   * Whether the carrier bears the customer-default risk. `true` =
   * carrier covers shortfall if the customer doesn't pay; `false` =
   * lender absorbs the loss.
   */
  recourse: z.boolean(),
  /** Free-form note from the lender, surfaced verbatim to the carrier. */
  notes: z.string().trim().optional(),
});

export type FinancingOfferTerms = z.infer<typeof financingOfferTermsSchema>;

/**
 * One lender's response on a `FinancingApplication`. Each entry IS
 * the offer — there is no separate `FinancingOffer` collection in
 * v0. Inline in `FinancingApplication.offers[]` so the lender's
 * inbox query (`GET /financing/lender/inbox`) is a single doc read
 * per row.
 */
export const financingOfferSchema = z.object({
  /**
   * Stable per-offer id (idSchema string, never `_id`). Used in
   * URLs and per-row uniqueness anchors.
   */
  id: idSchema,

  /**
   * Embedded lender reference + display snapshot.
   *
   * - `_id` is the canonical pointer to `lenders._id` — used by the
   *   public lender-view route (`?i=<lenderId>`) and the lender
   *   directory lookup. Stored as an ObjectId so a one-shot
   *   `$lookup` against the Lender collection is cheap.
   * - `name` is the inline display snapshot so the offer card on
   *   the carrier dashboard still renders if the Lender doc is
   *   rotated, renamed, or removed. The full live catalog (logo,
   *   tagline, website, brand color) comes from `GET /lenders/active`;
   *   this is the resilient fallback.
   *
   * No `email` here — lender contact emails live on the Lender doc
   * (`Lender.contacts[]`) and are pulled by the notification service
   * at fan-out time, not embedded per-offer.
   */
  lender: z.object({
    _id: objectIdSchema,
    name: nameSchema,
  }),

  /** When the application packet was sent to this lender. */
  sentAt: dateSchema,
  /** When the lender first opened the link. Best-effort. */
  openedAt: dateSchema.optional(),
  /**
   * When the offer's `decision` last changed (lender's
   * `offered` / `declined` submit, or carrier's `accepted`
   * flip). Stamped server-side on every write.
   */
  decidedAt: dateSchema.optional(),

  /**
   * Lifecycle state of this offer row.
   *
   * Writers:
   *  - **Lender** flips `pending → interested` via the dedicated
   *    `partnerExpressInterestAPI` / `lenderExpressInterestAPI`
   *    endpoint (sets `decision: 'interested'` and the
   *    `interestedAt` stamp atomically).
   *  - **Lender** flips `pending | interested → offered | declined`
   *    via the partner-view / lender-view submit endpoints.
   *  - **Carrier / super-admin** flips `offered → accepted` via
   *    `acceptOfferAPI`, or via the soft-accept branch of
   *    `markOutcomeAPI` (`kind: 'funded'`). The lender-facing API
   *    schemas forbid `'accepted'` so a lender cannot self-promote.
   *
   * Invariant: at most one offer per application carries
   * `decision: 'accepted'`. The accept service enforces it.
   */
  decision: financingOfferDecisionSchema.default('pending'),

  /**
   * Stamp set when the partner clicks "I'm interested" on the
   * partner-view page. Written in lockstep with the
   * `decision: 'pending' → 'interested'` flip — the timestamp is the
   * audit trail, the decision is the live state. Cleared back to
   * `undefined` is not supported; once a partner sees the contact
   * info, they've seen it. Preserved through later `offered` /
   * `accepted` transitions so the historic engagement order is not
   * lost when terms are submitted.
   *
   * Reads (e.g. contact-masking on `GET /financing/partner/:appId`)
   * should treat `decision !== 'pending'` as the live "contact
   * revealed" signal; this stamp is the historic trail.
   */
  interestedAt: dateSchema.optional(),

  /**
   * Present iff `decision ∈ { 'offered', 'accepted' }`. Locked
   * at the moment the lender submits terms; the carrier accept
   * flow flips `decision` but does NOT touch the terms — the
   * accepted offer carries the exact numbers the carrier saw.
   */
  terms: financingOfferTermsSchema.optional(),

  /**
   * Derived server-side: `true` iff the lender's terms differ from
   * the carrier's request on amount OR repayment term. Lets the
   * carrier eyeball "this is a counter, not exactly what I asked
   * for" in the UI without computing it client-side.
   */
  isCounterOffer: z.boolean().optional(),

  /** Required iff `decision = 'declined'`. */
  declineReason: financingDeclineReasonSchema.optional(),
  /** Optional free-text alongside the structured `declineReason`. */
  declineNotes: z.string().trim().optional(),
});

export type FinancingOffer = z.infer<typeof financingOfferSchema>;
